import React, {useState, useEffect, useMemo, useCallback} from "react";
import Box from "@mui/material/Box";
import {useAuth} from "hooks/useAuth";
import {APIGetTestsHistory} from "features/tests/listing/api/tests";
import CustomDataTable from "shared/tables/CustomDataTable";
import moment from "moment";
import { Link } from "react-router-dom";
import GenericButton from "shared/ui/GenericButton";
import {getRomanNumber, isOneOf} from "../../../../utils/functions";
import { TestHistory } from "./_interfaces";
import { getArchivedTableColumns, getHistoryTableColumns } from "./_tableColumns";
import { getSubjects } from "../../../../utils/subjects";
import { APIPostCustomItemsSubjects } from "../api/customSubcategories";
import useDataTable from "../../../../hooks/useDataTable";
import { TEST_TYPES, TestType } from "../../../../shared/types/testTypes";

interface Props {
    tabType?: string;
    type?: TestType;
    pageType?: string;
    pageId?: string;
    fromClassroom?: boolean;
}

const TestsSchoolHistory = ({type, tabType, pageType, pageId, fromClassroom = false}: Props) => {
    const {update, user} = useAuth();
    const [subjectsFilterOptions, setSubjectsFilterOptions] = useState<any[]>(getSubjects('school'));

    const table = useDataTable("testsHistory", "test", type, pageType);

    const {
        setLoading,
        testType,
        page,
        order,
        orderBy,
        submittedQuery,
        startDate,
        endDate,
        filterValues,
        setTableData,
        setTableColumns,
        setPerPage,
        setTotalRows,
        currentFilters,
    } = table;

    const testConfig = type ? TEST_TYPES[type] : TEST_TYPES[testType];

    const testTypeFilterOptions = Object.values(TEST_TYPES).map((testType) => ({
        label: testType.name,
        value: testType.id,
    }));

    const openTestRoute = useCallback((
        action: "raport" | "raspunsuri",
        testId: number | string,
    ) => {
        // const basePath = getTestBasePath(testType);
        const basePath = tabType === "history" ? "/teste" : `/teste/istoric-teste`;

        window.open(`${basePath}/${action}/${testId}`);
    }, [testType]);

    const canViewReport = useCallback((test: TestHistory) =>
            testConfig.canShowReport &&
            test.test_type !== "Test rapid" &&
            !(
                type === "school" &&
                user.testsListingShow !== undefined &&
                !user.testsListingShow
            ),
        [user.testsListingShow, type]);

    const canViewAnswers = useCallback(() =>
                user.position === "principal" ||
                user.position === "teacher" ||
                (user.position === "student" && testType !== "customTest"),
        [testType, user.position]);

    const canViewLog = useCallback(() =>
            (user.position === "principal" || user.position === "teacher") &&
            tabType === "history",
        [tabType, user.position]);

    const mapTestToRow = useCallback((test: TestHistory) => {

        const isPrincipal = user.position === "principal";
        const isStudent = user.position === "student";
        const isStudentPage = pageType === "student";
        const isClassroomPage = pageType === "classroom";

        const usesGrade = testConfig.usesGrade;

        const showScore = !isOneOf(testType, ["training", "customTest"]);
        const classroomLabel = test.classroom_grade
            ? `(${getRomanNumber(test.classroom_grade)}) ${test.classroom_name}`
            : test.classroom_name;

        const actions = (
            <Box display="flex" justifyContent="flex-end">
                {canViewReport(test) && (
                    <Box mr={1}>
                        <GenericButton type="report" onClick={() => openTestRoute("raport", test.test)} />
                    </Box>
                )}

                {canViewAnswers() && (
                    <Box mr={1}>
                        <GenericButton type="answers" onClick={() => openTestRoute("raspunsuri", test.test)} />
                    </Box>
                )}

                {canViewLog() && (
                    <Box>
                        <GenericButton type="log" onClick={() => window.open(`/teste/log/${test.test}`)} />
                    </Box>
                )}
            </Box>
        );

        return {
            ...(isClassroomPage && {type: testType}),

            ...(usesGrade
                ? {test_grade: test.test_grade}
                : {test_type: `${test.test_type} - ${test.test_grade}`}),

            test_started_at: test.test_closed_at
                ? moment(test.test_closed_at).locale("ro").format("HH:mm, DD.MM.YYYY")
                : "Nedeschis",

            ...(!isStudent && !isStudentPage && {
                user_name: <Link to={`/elevi/${test.user_id}`}>{test.user_name}</Link>,
            }),

            classroom_name: isStudent && !isStudentPage && !isClassroomPage
                ? classroomLabel
                : <Link to={`/clase/${test.classroom_id}`}>{classroomLabel}</Link>,

            ...(showScore && {
                test_score:
                    testType === "school" &&
                    user.testsListingShow !== undefined &&
                    !user.testsListingShow
                        ? "-"
                        : test.test_score,
            }),

            responses: `${test.test_right_answers}/${test.test_total_answers}`,
            test_time: test.test_time,

            ...((isPrincipal || isStudent) && {
                owner_name: isPrincipal
                    ? <Link to={`/profesori/${test.owner_id}`}>{test.owner_name}</Link>
                    : test.owner_name,
            }),

            ...(!isStudent && {test_tag: test.test_tag}),

            test: test.test,
            actions,
        };
    }, [canViewAnswers,
        canViewLog,
        canViewReport,
        openTestRoute,
        pageType,
        testType,
        user.position,
        user.testsListingShow,]);

    useEffect(() => {
        if (testType === "customTest") {
            APIPostCustomItemsSubjects()
                .then((response) => {
                    if (response.data.success) {
                        const items = response.data?.data?.data?.items ?? [];

                        setSubjectsFilterOptions(
                            items.map((item: any) => ({
                                label: item.name,
                                value: item.id,
                            })),
                        );
                    } else {
                        update("error", {show: true, code: "A002"});
                    }
                })
                .catch(() => update("error", {show: true, code: "A001"}));
        } else {
            // const newSubjects = getSubjects(testType).filter(
            //     (subject) => subject.newStandardId !== 1 && subject.newStandardId !== 3
            // );

            setSubjectsFilterOptions(getSubjects(testType));
        }

    }, [testType]);

    useEffect(() => {
        setLoading(true);
        APIGetTestsHistory({
            page: `?page=${page + 1}`,
            sort: orderBy,
            order: order,
            search: submittedQuery,
            startDate: startDate,
            endDate: endDate,
            type: testType,
            pageType: pageType,
            pageTypeId: pageId,
            tab_type: tabType,
            ...filterValues,
        })
            .then((response) => {
                if (response.data.success) {
                    // console.log("getActiveTests OK", response);
                    setTableData(response.data.data.data.map((test: any) => mapTestToRow(test)));
                    setTotalRows(response.data.data.total);
                    setPerPage(response.data.data.per_page);
                } else {
                    update("error", {show: true, code: "A002"});
                }
            })
            .catch(() => update("error", {show: true, code: "A001"}))
            .finally(() => setLoading(false));
    }, [page, orderBy, order, submittedQuery, currentFilters]);

    useEffect(() => {
        const columns = tabType === "history" ? getHistoryTableColumns(user.position, pageType, testType) : getArchivedTableColumns(user.position, testType);
            setTableColumns(columns);
    }, [user.position, pageType, testType]);

    const exportConfig = useMemo(() => ({
        buildRequestParams: () => ({
            page: `?page=${page + 1}`,
            sort: orderBy,
            order: order,
            search: submittedQuery,
            startDate: startDate,
            endDate: endDate,
            type: testType,
            export: true,
            tab_type: "history",
            ...filterValues,
        }),
        fetchData: async (params: any) => {
            const response = await APIGetTestsHistory(params);
            if (!response.data.success || !response.data.data) {
                return [];
            }
            return response.data.data.export;
        },
        isExportingFromBackend: true,
    }), [endDate, order, page, startDate, testType, submittedQuery, filterValues,]);

    return (
        <CustomDataTable
            loading={table.loading}
            columns={table.tableColumns}
            rows={table.tableData}
            rowsPerPage={table.perPage}
            page={page}
            totalRows={table.totalRows}
            onPageChange={table.handleChangePage}
            handleRequestSort={table.handleRequestSort}
            orderBy={orderBy}
            order={order}
            hasFilters={true}
            onChangeFilters={table.handleFilterChange}
            onCancelFilters={table.handleCancelFilters}
            onApplyFilters={table.handleApplyFilters}
            activeColumnIds={table.activeColumnIds}
            onActiveColumnIdsChange={table.setActiveColumnIds}
            resetFormData={table.handleResetFilters}
            filterValues={filterValues}
            hasExport={!fromClassroom && tabType === "history" && user.position !== "student"}
            exportData={exportConfig}
            filterOptions={{test_grade: subjectsFilterOptions, test_type: subjectsFilterOptions, type: testTypeFilterOptions}}
            hasSearch={true}
            onSearch={table.handleSearch}
            onChange={table.handleSearchChange}
            searchValue={table.query}
            onClearSearch={table.handleClearSearch}
        />
    );
};

export default TestsSchoolHistory;